/**
 * Canonical contract for where a submission's score lives.
 *
 * A submission never carries a top-level `score` field — the value is nested
 * inside `submittedData` (a.k.a. survey `responses`) under one of these keys.
 * Keeping the list here means the Daily Journey history table and the
 * SubmissionViewer's FormDisplay read the exact same candidates and cannot
 * drift apart (SIMS-7103).
 */
export const SCORE_FIELD_CANDIDATES = [
	"totalScore",
	"score",
	"calculatedScore",
] as const;

/**
 * Pull the numeric score out of a submission's `submittedData`/`responses`
 * record, trying each candidate key in order.
 *
 * Returns `undefined` when there is no score (no record, or none of the
 * candidate keys hold a number). Note that `0` is a VALID score and is
 * returned as `0` — callers must use a nullish check (`?? "-"`) rather than a
 * falsy check when rendering a fallback.
 */
export const extractScore = (
	submittedData: Record<string, unknown> | undefined | null,
): number | undefined => {
	if (!submittedData) return undefined;
	for (const key of SCORE_FIELD_CANDIDATES) {
		const value = submittedData[key];
		if (typeof value === "number" && !Number.isNaN(value)) {
			return value;
		}
	}
	return undefined;
};

/**
 * Resolve the score to display for a Daily Journey submission, across every
 * stored shape:
 *  - simple FORM: score at the top level, or nested under `formResponses`
 *    (SIMS-7241);
 *  - compound: `submittedData.steps` is a map of child submissions keyed by
 *    step event id — there is no single score, so show the FIRST child that
 *    carries one, in stored order (SIMS-7241).
 *
 * Returns `undefined` when no score exists anywhere. `0` is a valid score and
 * is returned as `0` — callers must use a nullish (`?? "-"`) fallback.
 */
export const extractSubmissionScore = (
	submittedData: Record<string, unknown> | undefined | null,
): number | undefined => {
	if (!submittedData) return undefined;

	// Simple form: prefer the nested `formResponses`, else the top-level blob.
	const formResponses = submittedData.formResponses as
		| Record<string, unknown>
		| undefined;
	const direct = extractScore(formResponses ?? submittedData);
	if (direct !== undefined) return direct;

	// Compound: first child step that has a score wins.
	const steps = submittedData.steps as
		| Record<string, Record<string, unknown>>
		| undefined;
	if (steps && typeof steps === "object") {
		for (const step of Object.values(steps)) {
			const stepResponses =
				(step?.formResponses as Record<string, unknown> | undefined) ?? step;
			const stepScore = extractScore(stepResponses);
			if (stepScore !== undefined) return stepScore;
		}
	}

	return undefined;
};
